/**
 * Switching the active account is a SESSION mutation, not a filter.
 *
 * The server decides what a request may read from the account claim in the
 * JWT, never from an id in the payload: a report asking for account B while
 * the cookie still says account A is refused with "Cannot access reports for
 * other accounts", however many roles the person holds in B. So any UI that
 * lets someone pick another account has to re-scope the session, and picking
 * one anywhere must follow the same steps in the same order:
 *
 *   1. gate dependent queries, so nothing fetches the new account under the
 *      old cookie
 *   2. move the client-side selection (URL + storage) and clear the location,
 *      which belongs to the account being left
 *   3. re-scope the JWT cookie — /auth/switch-account
 *   4. drop caches keyed by the old scope
 *   5. adopt a location in the NEW account and re-scope permissions to it,
 *      because a JWT carrying a location from the old account grants the
 *      wrong location-scoped permissions
 *
 * This module is the one implementation of that sequence. It lives apart from
 * App.tsx so every account picker (Reports, Executive, Departments) runs the
 * same steps: before, only the old top-bar picker ran it, and the reports picker set local state alone —
 * which is why choosing another account there always produced a 403.
 *
 * Failure is reported, never swallowed. A switch that fails at step 3 leaves
 * the person looking at account B while the cookie still says A, and every
 * subsequent call fails for a reason the screen does not explain.
 */
import { clearRoleLocks } from '../queries/roleLockScope';
import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { execQueryKeys } from '@turn-connect/shared';
import { queryKeys } from '../queries/queryKeys';
import { adminService } from '../services/auth';

export interface AccountSwitchResult {
  ok: boolean;
  /** Present when ok is false — safe to show to the user. */
  error?: string;
}

export interface AccountSwitchDeps {
  isSystemAdmin: boolean;
  /** Moves the client-side account selection (URL + localStorage). */
  selectAccount: (accountId: string) => void;
  selectLocation: (locationId: string | null) => void;
  updatePermissions: (permissions: string[], locationId?: string) => void;
  /** Gates queries that must not run against the old cookie scope. */
  setSwitchInFlight: (inFlight: boolean) => void;
  /**
   * Reads that same flag back. Switches are SERIALIZED: each one mutates
   * shared session state (account, location, permissions, caches), so two in
   * flight can finish out of order and leave the session scoped to the
   * account nobody picked last. A getter rather than the React state value,
   * because a second pick can arrive before any re-render — and it lives in
   * the deps because the top bar calls performAccountSwitch directly, which a
   * guard held only by the provider would not cover.
   */
  isSwitchInFlight: () => boolean;
  queryClient: QueryClient;
}

/**
 * Runs the account switch end to end. Resolves ok:false when the session
 * could not be re-scoped — the caller decides how loudly to say so.
 *
 * ONE server call: /auth/switch-context moves the cookie into the account,
 * lands on the last location the person used there (else their first, else
 * the account view) and returns that context with its permissions. It used
 * to be two calls — switch-account, then switch-location to the first
 * location in API order — so people landed on a shop they had never picked,
 * and a system admin's cookie never moved at all.
 */
export async function performAccountSwitch(deps: AccountSwitchDeps, accountId: string): Promise<AccountSwitchResult> {
  const { selectAccount, selectLocation, updatePermissions, setSwitchInFlight, isSwitchInFlight, queryClient } = deps;
  if (!accountId) return { ok: false, error: 'No account selected' };
  if (isSwitchInFlight()) {
    return { ok: false, error: 'Still switching accounts — try again in a moment' };
  }

  setSwitchInFlight(true);
  try {
    // THE COOKIE MOVES FIRST. Moving the selection first republishes the
    // account id to everything watching it while the cookie is still on the
    // old account, and those reads are refused.
    let result: Awaited<ReturnType<typeof adminService.switchContext>>;
    try {
      result = await adminService.switchContext({ accountId });
    } catch (err) {
      // Selection untouched: the session never moved, so neither does the UI.
      return {
        ok: false,
        error: err instanceof Error ? err.message : 'Could not switch to that account',
      };
    }

    // Adopt what the server resolved, not a guess of our own.
    const { context, permissions } = result;
    selectAccount(context.account_id);
    selectLocation(context.location_id);
    // Apply even when empty: fewer permissions at the new place must hide UI.
    updatePermissions(permissions, context.location_id ?? undefined);

    void queryClient.invalidateQueries({ queryKey: queryKeys.locations.byAccount(context.account_id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.accounts.mine });
    // The Executive dashboard answers for the session's account without naming
    // it in its query, so its cached figures belong to the account just left:
    // drop them rather than show them under the new account's name.
    queryClient.removeQueries({ queryKey: execQueryKeys.all });
    // Role locks answered for the account just left go too.
    clearRoleLocks(queryClient);
    return { ok: true };
  } finally {
    // Held until the new location is adopted, so nothing refetches against a
    // half-moved session.
    setSwitchInFlight(false);
  }
}

interface AccountSwitchContextValue {
  switchToAccount: (accountId: string) => Promise<AccountSwitchResult>;
  /**
   * Moves the session to a location and its account (App's location switch),
   * resolving true when it did. Absent where App did not provide it.
   */
  switchToLocation?: (locationId: string) => Promise<boolean>;
  isSwitching: boolean;
}

const AccountSwitchContext = createContext<AccountSwitchContextValue | undefined>(undefined);

/**
 * Provides the switch to anything rendered below it. App owns the
 * dependencies (selection hooks, auth, query client) and passes them in once.
 */
export const AccountSwitchProvider: React.FC<{
  deps: AccountSwitchDeps;
  switchToLocation?: (locationId: string) => Promise<boolean>;
  children: ReactNode;
}> = ({ deps, switchToLocation, children }) => {
  const [isSwitching, setIsSwitching] = useState(false);

  // performAccountSwitch holds the serialization guard itself, so a second
  // pick is refused there rather than here; this flag only drives the UI.
  const switchToAccount = useCallback(
    async (accountId: string) => {
      setIsSwitching(true);
      try {
        return await performAccountSwitch(deps, accountId);
      } finally {
        setIsSwitching(false);
      }
    },
    [deps]
  );

  const value = useMemo(() => ({ switchToAccount, switchToLocation, isSwitching }), [switchToAccount, switchToLocation, isSwitching]);
  return <AccountSwitchContext.Provider value={value}>{children}</AccountSwitchContext.Provider>;
};

/**
 * Returns the switch, or undefined outside the provider. Undefined is a
 * legitimate answer — a surface with no way to re-scope the session should
 * hide its account picker rather than offer a choice that cannot work.
 */
export function useAccountSwitch(): AccountSwitchContextValue | undefined {
  return useContext(AccountSwitchContext);
}
